import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { Pictogram } from '@cbhq/cds-web/illustrations/Pictogram';
import { Box } from '@cbhq/cds-web/layout/Box';
import { alertModalWidth } from '@cbhq/cds-web/overlays/Alert';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { Portal } from '@cbhq/cds-web/overlays/Portal';
import { alertContainerId } from '@cbhq/cds-web/overlays/PortalProvider';
import { Text } from '@cbhq/cds-web/typography/Text';

type RemoveMemberModalProps = {
  visible: boolean;
  onClose: () => void;
  memberName: string;
  onConfirm: () => void;
  loading?: boolean;
};

const messages = defineMessages({
  removeMemberModalTitle: {
    id: 'Team.removeMemberModalTitle',
    defaultMessage: 'Remove team member?',
    description: 'Remove member modal title',
  },
  removeMemberModalDescription: {
    id: 'Team.removeMemberModalDescription',
    defaultMessage: "{memberName} will no longer have access to any of your company's data.",
    description: 'Remove member modal description',
  },
  removeMemberModalButton: {
    id: 'Team.removeMemberModalButton',
    defaultMessage: 'Remove',
    description: 'Remove member modal button',
  },
  removeMemberModalCancel: {
    id: 'Team.removeMemberModalCancel',
    defaultMessage: 'Cancel',
    description: 'Remove member modal cancel',
  },
});

const TEST_ID = 'team-member-remove-alert';
// Only one remove-member alert renders at a time, so a static id is unique.
const TITLE_ID = `${TEST_ID}-title`;

// Rebuilds CDS Alert's layout from its parts, because CDS Alert has no loading
// state for its buttons. Unlike CDS Alert, confirming calls only onConfirm and
// leaves closing to the caller, so the alert stays open while the removal runs.
export function RemoveMemberAlert({
  visible,
  onClose,
  memberName,
  onConfirm,
  loading = false,
}: RemoveMemberModalProps) {
  const { formatMessage } = useIntl();

  const handleRequestClose = useCallback(() => {
    if (loading) {
      return;
    }

    onClose();
  }, [loading, onClose]);

  return (
    <Portal containerId={alertContainerId}>
      <Modal
        dangerouslyDisableResponsiveness
        disableOverlayPress
        disablePortal
        hideDividers
        accessibilityLabelledBy={TITLE_ID}
        dangerouslySetPosition="static"
        onRequestClose={handleRequestClose}
        role="alertdialog"
        shouldCloseOnEscPress={!loading}
        testID={TEST_ID}
        visible={visible}
        width={alertModalWidth}
      >
        <Box
          alignItems="center"
          flexDirection="column"
          paddingBottom={1}
          paddingTop={3}
          paddingX={3}
        >
          <Box paddingBottom={2}>
            <Pictogram
              dimension="48x48"
              name="idError"
              scaleMultiplier={2.5}
              testID={`${TEST_ID}-pictogram`}
            />
          </Box>
          <Text
            as="h3"
            display="block"
            font="title3"
            id={TITLE_ID}
            paddingBottom={0.5}
            textAlign="center"
          >
            {formatMessage(messages.removeMemberModalTitle)}
          </Text>
          <Text as="p" color="fgMuted" display="block" font="body" textAlign="center">
            {formatMessage(messages.removeMemberModalDescription, { memberName })}
          </Text>
        </Box>
        <Box flexDirection="column" gap={1} paddingX={3} paddingY={2} testID={`${TEST_ID}-actions`}>
          <Button block loading={loading} onClick={onConfirm} variant="negative">
            {formatMessage(messages.removeMemberModalButton)}
          </Button>
          <Button block disabled={loading} onClick={handleRequestClose} variant="secondary">
            {formatMessage(messages.removeMemberModalCancel)}
          </Button>
        </Box>
      </Modal>
    </Portal>
  );
}
